import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { subYears } from 'date-fns';
import posthog from 'posthog-js';
import { Navigate } from 'react-router-dom';

import { toggleModal } from '../../../features/ui/uiSlice';
import { states } from '../../../utils/constants';
import {
    CustomCheckbox,
    CustomReactSelect,
    CustomInput,
    DatePickerField,
} from '../../InputComponents';
import { useCreateUserMutation } from '../../../app/services/auth';
import { useGetWebflowTermsQuery } from '../../../app/services/webflow';
import { useAssessmentContext } from '../../../providers';

//Images
import { BsQuestionCircle } from 'react-icons/bs';

const eighteenYearsAgo = subYears(new Date(), 18);
const fiftyYearsAgo = subYears(new Date(), 50);

function CreateAccountForm() {
    const dispatch = useDispatch();

    //Queries and Mutations
    const { data: webflowTerms } = useGetWebflowTermsQuery();
    const [createUser, { error, isSuccess, isLoading }] = useCreateUserMutation();

    //Contexts
    const { answers, homeSleepStudyStatus, partnerId } = useAssessmentContext();

    async function submitHandler(formData) {
        const informedConsentData = webflowTerms?.items?.find((t) => t.slug === 'consent');
        const consentsPayload = informedConsentData
            ? [
                  {
                      id: informedConsentData._id,
                      version: '' + informedConsentData?.version,
                      name: informedConsentData.name,
                      documentType: informedConsentData.slug,
                  },
              ]
            : [];
        const formDataClone = { ...formData };
        const answersClone = {
            ...answers,
            //This question comes in two parts, but we mark true if they answer either as YES
            doesStopBreathing: answers.doesStopBreathing || answers.doesStopBreathing2,
        };

        formDataClone.phone = formDataClone.phone.replace(/[- )(]/g, '');
        formDataClone.firstName = formDataClone.firstName.trim();
        formDataClone.lastName = formDataClone.lastName.trim();
        formDataClone.stopBangResult = answersClone;
        formDataClone.consents = consentsPayload;
        formDataClone.referFromOrganizationId = partnerId || undefined;
        if (homeSleepStudyStatus) formDataClone.homeSleepStudyStatus = homeSleepStudyStatus;

        formDataClone.redirectUrl = window.location.origin;

        delete formDataClone.confirmPassword;
        delete formDataClone.confirmEmail;
        delete formDataClone.stopBangResult.highRiskSelections;
        delete formDataClone.stopBangResult.doesStopBreathing2;

        //Add posthog session ID to submission
        const postHogSessionID = posthog?.get_session_id();
        if (postHogSessionID) {
            formDataClone.postHogSessionID = postHogSessionID;
        }

        posthog?.capture('Submitted Create Account Form');

        const response = await createUser(formDataClone);

        if (response?.data) {
            posthog?.identify(response?.data?.id);
        }
    }

    const createAccountSchema = yup
        .object({
            firstName: yup.string().strict(true).min(1).required('First name is required'),
            lastName: yup.string().strict(true).min(1).required('Last name is required'),
            state: yup.string().required('State is required.'),
            birthdate: yup
                .date()
                .max(eighteenYearsAgo, 'You must be at least 18 years old')
                .required('Date of Birth is required')
                .nullable(),
            email: yup
                .string()
                .email('Must be a valid email address.')
                .required('Email is required.'),
            confirmEmail: yup
                .string()
                .email('Must be a valid email address.')
                .required('You must enter your email twice')
                .equals([yup.ref('email')], 'Emails must match.'),
            phone: yup
                .string()
                .transform((val) => {
                    const v = val;
                    return v.replace(/[- )(]/g, '');
                })
                .required('Phone number is required')
                .matches(/^\d{10}$/, 'Phone number must be 10 digits'),
            password: yup
                .string()
                .required('Password is required')
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*-])(?=.{8,})/,
                    'Must contain 8 characters, one uppercase, one lowercase, one number and one special character (!@#$%^&*-)',
                ),
            confirmPassword: yup
                .string()
                .required('Password confirm is required')
                .equals([yup.ref('password')], 'Passwords must match.'),
            agreeToPrivacy: yup.boolean().oneOf([true], 'You must agree to this'),
            agreeToTerms: yup.boolean().oneOf([true], 'You must agree to this'),
        })
        .required();

    const privacyLabel = () => (
        <>
            I agree to the{' '}
            <button
                className="border-b-2 border-green-400 font-bold text-white"
                onClick={(ev) => {
                    ev.preventDefault();
                    dispatch(toggleModal('notice'));
                }}>
                privacy policy
            </button>
        </>
    );

    const termsLabel = () => (
        <>
            I have read and agree to the{' '}
            <button
                className="border-b-2 border-green-400 font-bold text-white"
                onClick={(ev) => {
                    ev.preventDefault();
                    dispatch(toggleModal('informedConsent'));
                }}>
                Informed Medical Consent
            </button>
        </>
    );

    const marketingLabel = () => (
        <>
            I agree to receive GEM Health{' '}
            <button
                className="border-b-2 border-green-400 font-bold text-white"
                onClick={(ev) => {
                    ev.preventDefault();
                    dispatch(toggleModal('marketing'));
                }}>
                Marketing emails/SMS messages
            </button>
        </>
    );

    return isSuccess ? (
        <Navigate to="/account-created" state={{ fromSaveAssessment: true }} />
    ) : (
        <Formik
            validationSchema={createAccountSchema}
            validateOnChange={true}
            initialValues={{
                firstName: '',
                lastName: '',
                state: '',
                birthdate: null,
                email: '',
                confirmEmail: '',
                phone: '',
                patientReportedInsurance: '',
                password: '',
                confirmPassword: '',
                agreeToPrivacy: false,
                agreeToTerms: false,
                agreeToMarketing: true,
            }}
            onSubmit={submitHandler}>
            {({ dirty, isValid }) => (
                <Form
                    id="gem-create-account"
                    name="gem-create-account"
                    className="ph-no-capture space-y-6">
                    <CustomInput name="firstName" label="First Name" />
                    <CustomInput name="lastName" label="Last Name" />
                    <CustomInput type="email" name="email" label="Email" autoComplete="username" />
                    <CustomInput
                        type="email"
                        name="confirmEmail"
                        label="Confirm your Email"
                        autoComplete="username"
                    />
                    <CustomInput
                        type="password"
                        name="password"
                        label="Password"
                        autoComplete="new-password"
                        className="ph-no-capture"
                    />
                    <CustomInput
                        type="password"
                        name="confirmPassword"
                        label="Confirm password"
                        autoComplete="new-password"
                        className="ph-no-capture"
                    />
                    <div className="relative">
                        <DatePickerField
                            name="birthdate"
                            dateFormat="MM/dd/yyyy"
                            maxDate={eighteenYearsAgo}
                            openToDate={fiftyYearsAgo}
                            showYearDropdown
                            yearDropdownItemNumber={100}
                            placeholderText="Birthdate"
                        />
                        <BsQuestionCircle
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(toggleModal('birthdateHelp'));
                            }}
                            className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-white hover:cursor-pointer hover:text-gem-green"
                        />
                    </div>
                    <CustomReactSelect
                        id="state"
                        name="state"
                        placeholder="State"
                        options={states}
                    />
                    <CustomInput name="phone" label="Mobile Phone" inputMode="numeric" />
                    <CustomCheckbox
                        name="agreeToPrivacy"
                        id="agreeToPrivacy"
                        label={privacyLabel()}
                    />
                    <CustomCheckbox name="agreeToTerms" id="ageAgreement" label={termsLabel()} />
                    <CustomCheckbox
                        name="agreeToMarketing"
                        id="marketingAgreement"
                        label={marketingLabel()}
                    />
                    <button
                        className="btn-primary w-full"
                        type="submit"
                        disabled={!dirty || !isValid || isLoading}>
                        Continue
                    </button>
                    {error && (
                        <p className="mt-3 font-bold text-red">
                            Unable to create an account? CHECK YOUR EMAIL FOR NEXT STEPS.
                        </p>
                    )}
                </Form>
            )}
        </Formik>
    );
}

export default CreateAccountForm;
